import React, { useEffect, useState } from "react";
import { PageHeader, Card, Tag, fmt } from "../components/UI";
import DataTable from "../components/DataTable";
import { Network, Users, ShieldCheck, TrendingUp } from "lucide-react";
import api from "../lib/api";

function Kpi({ label, value, tone = "slate" }) {
  const toneMap = { slate: "text-slate-900", teal: "text-teal-700", emerald: "text-emerald-700", amber: "text-amber-700" };
  return (
    <div className="surface p-4">
      <div className="small-caps text-slate-400">{label}</div>
      <div className={`serif-display text-3xl mt-1 numerals ${toneMap[tone]}`}>{fmt(value || 0)}</div>
    </div>
  );
}

export default function HierarchyRollup() {
  const [data, setData] = useState(null);

  useEffect(() => { (async () => {
    const { data } = await api.get("/hierarchy/my-rollup");
    setData(data);
  })(); }, []);

  if (!data) return <div className="text-slate-500 p-6">Loading hierarchy rollup…</div>;

  const physioCols = [
    { key: "physio_code", label: "ERP ID", filter: "text", render: (r) => <span className="font-mono text-[11px]">{r.erp_outlet_id || r.physio_code}</span> },
    { key: "owner_name", label: "Physio", filter: "text",
      render: (r) => <div><div className="font-medium">{r.owner_name || `${r.first_name} ${r.last_name}`}</div><div className="text-[11px] text-slate-500">{r.clinic_name}</div></div> },
    { key: "erp_outlet_id", label: "Outlet ERP", filter: "text", render: (r) => <span className="font-mono text-[11px]">{r.erp_outlet_id}</span> },
    { key: "city", label: "City", filter: "text" },
    { key: "certified", label: "Certified", filter: "select", options: ["Yes", "No"],
      accessor: (r) => r.certified ? "Yes" : "No",
      render: (r) => r.certified ? <Tag tone="emerald"><ShieldCheck size={11} /> Yes</Tag> : <Tag tone="slate">No</Tag> },
    { key: "total_orders", label: "Total Orders", filter: "range", align: "right", render: (r) => fmt(r.total_orders) },
    { key: "active_orders", label: "Active", filter: "range", align: "right",
      render: (r) => <Tag tone="emerald">{fmt(r.active_orders)}</Tag> },
    { key: "pending_orders", label: "Pending", filter: "range", align: "right",
      render: (r) => <Tag tone="amber">{fmt(r.pending_orders)}</Tag> },
    { key: "active_points", label: "Active Pts", filter: "range", align: "right", render: (r) => fmt(r.active_points) },
    { key: "pending_points", label: "Pending Pts", filter: "range", align: "right", render: (r) => fmt(r.pending_points) },
  ];

  const reportsCols = [
    { key: "erp_id", label: "ERP Id", filter: "text", render: (r) => <span className="font-mono text-[12px]">{r.erp_id}</span> },
    { key: "name", label: "Name", filter: "text" },
    { key: "level_code", label: "Level", filter: "select", options: ["L1","L2","L3","L4","L5","L6"],
      render: (r) => <Tag tone="teal">{r.level_code}</Tag> },
    { key: "role", label: "Role", filter: "text" },
    { key: "territory", label: "Territory", filter: "text" },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        kicker={`${data.me.level_code || "Hierarchy"} · ${data.me.role || ""}`}
        title={`${data.me.name || data.me.erp_id || "My"} — Territory Rollup`}
        subtitle={`Total orders against physios mapped to your role, with Active vs Pending bifurcation. Territory: ${data.me.territory || "—"}.`}
        testId="rollup-page"
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi label="Physios" value={data.summary.physio_count} tone="slate" />
        <Kpi label="Total Orders" value={data.summary.total_orders} tone="slate" />
        <Kpi label="Active Orders" value={data.summary.active_orders} tone="emerald" />
        <Kpi label="Pending Orders" value={data.summary.pending_orders} tone="amber" />
        <Kpi label="Active Points" value={data.summary.active_points} tone="teal" />
        <Kpi label="Pending Points" value={data.summary.pending_points} tone="amber" />
      </div>

      {data.direct_reports?.length > 0 && (
        <Card>
          <div className="flex items-center gap-2 mb-3">
            <Users size={14} className="text-slate-600" />
            <h3 className="text-[14px] font-medium">My Direct Reports ({data.direct_reports.length})</h3>
          </div>
          <DataTable columns={reportsCols} data={data.direct_reports} rowKey="id"
            searchableKeys={["erp_id", "name", "territory"]} exportFilename="cbc-direct-reports" testIdPrefix="reports" />
        </Card>
      )}

      <Card>
        <div className="flex items-center gap-2 mb-3">
          <Network size={14} className="text-slate-600" />
          <h3 className="text-[14px] font-medium">Physios in my scope ({data.physios.length})</h3>
          <span className="text-[11px] text-slate-500 ml-auto inline-flex items-center gap-1"><TrendingUp size={11} /> Order activity rolls up from all assigned physios</span>
        </div>
        <DataTable columns={physioCols} data={data.physios} rowKey="id"
          searchableKeys={["owner_name", "clinic_name", "erp_outlet_id", "physio_code", "city"]}
          exportFilename="cbc-my-physios" testIdPrefix="my-physios"
          initialSort={{ key: "active_points", dir: "desc" }} />
      </Card>
    </div>
  );
}
